import { getVehicle } from "@fleetminder/contracts";
import {
  Avatar,
  AvatarFallback,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  ModeToggle,
  Separator,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  Skeleton,
  Spinner,
} from "@fleetminder/ui";

import { useQuery } from "@tanstack/react-query";
import {
  ChevronsUpDownIcon,
  CircleAlertIcon,
  HistoryIcon,
  LayoutDashboardIcon,
  LogOutIcon,
  MailIcon,
  ReceiptTextIcon,
  SettingsIcon,
  TruckIcon,
  UserRoundIcon,
  UsersIcon,
  WrenchIcon,
} from "lucide-react";
import {
  Component,
  type ErrorInfo,
  type PropsWithChildren,
  type ReactNode,
  useEffect,
} from "react";
import {
  Link,
  Outlet,
  useLocation,
  useMatches,
  useNavigate,
  useParams,
} from "react-router-dom";

import { callApi, queryKeys } from "./api-client.js";
import { OrganizationSwitcher, useActiveOrganization } from "./organization.js";
import { useSession } from "./session.js";

export function GlobalLoading() {
  return (
    <div
      className="flex min-h-[60vh] items-center justify-center gap-3 text-sm text-muted-foreground"
      role="status"
    >
      <Spinner className="size-5" />
      Loading FleetMinder
    </div>
  );
}

/** Skeleton shaped like a list page, for route-level suspense fallbacks. */
export function ListPageSkeleton() {
  return (
    <div aria-busy="true" className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-8 w-64" />
      </div>
      <Skeleton className="h-10 w-full" />
      <div className="space-y-2">
        {[0, 1, 2, 3, 4].map((row) => (
          <Skeleton className="h-14 w-full" key={row} />
        ))}
      </div>
    </div>
  );
}

type MembershipRole = "admin" | "member" | "owner";

type NavItem = {
  readonly end?: boolean;
  readonly icon: typeof TruckIcon;
  readonly label: string;
  /** Roles that may see the link. Omitted means every member. */
  readonly roles?: readonly MembershipRole[];
  readonly to: string;
};

/**
 * Hiding a link is a courtesy, not a control: the API authorizes every request
 * on its own. Navigation is filtered so the workspace matches what the member
 * can actually do, and nothing here is relied on for access.
 */
function buildNavigation(
  organizationId: string,
  role: MembershipRole | undefined,
): {
  readonly company: readonly NavItem[];
  readonly fleet: readonly NavItem[];
} {
  const base = `/app/organizations/${organizationId}`;
  const visible = (items: readonly NavItem[]) =>
    items.filter((item) => !item.roles || (role && item.roles.includes(role)));

  return {
    company: visible([
      { end: true, icon: LayoutDashboardIcon, label: "Dashboard", to: base },
      {
        end: true,
        icon: MailIcon,
        label: "Team",
        roles: ["admin", "owner"],
        to: `${base}/invitations`,
      },
      {
        end: true,
        icon: HistoryIcon,
        label: "Activity",
        to: `${base}/activity`,
      },
    ]),
    fleet: visible([
      { icon: TruckIcon, label: "Vehicles", to: `${base}/vehicles` },
      { icon: UsersIcon, label: "Drivers", to: `${base}/drivers` },
      { icon: WrenchIcon, label: "Maintenance", to: `${base}/maintenance` },
      {
        icon: ReceiptTextIcon,
        label: "Service history",
        to: `${base}/services`,
      },
      { icon: CircleAlertIcon, label: "Issues", to: `${base}/issues` },
    ]),
  };
}

function isActivePath(pathname: string, to: string, end?: boolean): boolean {
  return end === true
    ? pathname === to
    : pathname === to || pathname.startsWith(`${to}/`);
}

function NavSection({
  items,
  label,
}: {
  readonly items: readonly NavItem[];
  readonly label: string;
}) {
  const { pathname } = useLocation();

  return (
    <SidebarGroup>
      <SidebarGroupLabel>{label}</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map(({ end, icon: Icon, label: itemLabel, to }) => (
            <SidebarMenuItem key={to}>
              <SidebarMenuButton
                asChild
                isActive={isActivePath(pathname, to, end)}
                tooltip={itemLabel}
              >
                <Link to={to}>
                  <Icon aria-hidden />
                  <span>{itemLabel}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

type BreadcrumbHandle = {
  readonly breadcrumb?: string;
  /** Show the vehicle's own name instead of the fixed label. */
  readonly vehicleBreadcrumb?: boolean;
};

/**
 * Reads the same cached query the vehicle page loads, so naming the crumb
 * costs no extra request.
 */
function VehicleCrumbLabel({
  fallback,
  organizationId,
  vehicleId,
}: {
  readonly fallback: string;
  readonly organizationId: string;
  readonly vehicleId: string;
}) {
  const { data } = useQuery({
    queryFn: () =>
      callApi(getVehicle, { params: { organizationId, vehicleId } }),
    queryKey: queryKeys.vehicle(organizationId, vehicleId),
  });

  return <>{data?.vehicle.internalLabel ?? fallback}</>;
}

function ShellBreadcrumb() {
  const matches = useMatches();
  const trail = matches.flatMap((match) => {
    const handle = match.handle as BreadcrumbHandle | undefined;
    if (!handle?.breadcrumb) return [];
    const { organizationId, vehicleId } = match.params;
    const label: ReactNode =
      handle.vehicleBreadcrumb && organizationId && vehicleId ? (
        <VehicleCrumbLabel
          fallback={handle.breadcrumb}
          organizationId={organizationId}
          vehicleId={vehicleId}
        />
      ) : (
        handle.breadcrumb
      );

    return [{ label, pathname: match.pathname }];
  });

  if (trail.length === 0) {
    return null;
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {trail.map((entry, index) => {
          const isLast = index === trail.length - 1;

          return (
            <BreadcrumbItem key={entry.pathname}>
              {isLast ? (
                <BreadcrumbPage>{entry.label}</BreadcrumbPage>
              ) : (
                <>
                  <BreadcrumbLink asChild>
                    <Link to={entry.pathname}>{entry.label}</Link>
                  </BreadcrumbLink>
                  <BreadcrumbSeparator />
                </>
              )}
            </BreadcrumbItem>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
}

export function AuthenticatedShell() {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const { organizations, signOut, user } = useSession();
  const activeOrganization = useActiveOrganization();
  const activeOrganizationId =
    organizationId ?? organizations[0]?.organizationId ?? "";
  const navigation = buildNavigation(
    activeOrganizationId,
    activeOrganization?.role,
  );

  async function handleSignOut() {
    await signOut();
    navigate("/auth/sign-in", { replace: true });
  }

  return (
    <SidebarProvider>
      <Sidebar collapsible="icon" variant="inset">
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <OrganizationSwitcher />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        <SidebarContent>
          {navigation.company.length > 0 ? (
            <NavSection items={navigation.company} label="Company" />
          ) : null}
          {navigation.fleet.length > 0 ? (
            <NavSection items={navigation.fleet} label="Fleet" />
          ) : null}
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  {/* Named explicitly: the visible text is the member's own
                      name, which tells a screen reader nothing about what the
                      control does. */}
                  <SidebarMenuButton aria-label="Account menu" size="lg">
                    <Avatar className="size-8 rounded-lg">
                      <AvatarFallback className="rounded-lg">
                        {user ? (
                          initials(user.name)
                        ) : (
                          <UserRoundIcon aria-hidden className="size-4" />
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left leading-tight">
                      <span className="truncate font-medium">
                        {user?.name ?? "Your account"}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">
                        {user?.email ?? "Your account"}
                      </span>
                    </div>
                    <ChevronsUpDownIcon aria-hidden className="ml-auto" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" side="top">
                  <DropdownMenuLabel className="truncate">
                    {user?.email ?? "Your account"}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/app/account">
                      <SettingsIcon aria-hidden />
                      Your account
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => void handleSignOut()}
                    variant="destructive"
                  >
                    <LogOutIcon aria-hidden />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>

      <SidebarInset>
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 border-b bg-background/80 px-4 backdrop-blur">
          <SidebarTrigger className="-ml-1" />
          <Separator className="mr-2 h-4" orientation="vertical" />
          <ShellBreadcrumb />
          <div className="ml-auto">
            <ModeToggle />
          </div>
        </header>
        <div className="flex-1 p-4 md:p-6">
          <div className="mx-auto w-full max-w-6xl">
            <Outlet />
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

function StatusPage({
  action,
  description,
  eyebrow,
  title,
}: {
  readonly action?: ReactNode;
  readonly description: string;
  readonly eyebrow?: string;
  readonly title: string;
}) {
  return (
    <main className="flex min-h-screen items-center justify-center p-6">
      <Empty className="max-w-md">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <CircleAlertIcon aria-hidden />
          </EmptyMedia>
          {eyebrow ? (
            <p className="text-xs font-semibold tracking-widest text-primary uppercase">
              {eyebrow}
            </p>
          ) : null}
          <EmptyTitle>{title}</EmptyTitle>
          <EmptyDescription>{description}</EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          {action ?? (
            <Button asChild>
              <Link to="/">Return home</Link>
            </Button>
          )}
        </EmptyContent>
      </Empty>
    </main>
  );
}

export function RouteGroupErrorPage({
  error,
  routeGroup,
}: {
  readonly error?: unknown;
  readonly routeGroup: string;
}) {
  // The details are for us, not for the person looking at the page: a raw
  // JavaScript error or a route name tells a driver in a truck nothing.
  useEffect(() => {
    if (error !== undefined) {
      console.error("Route failed to load", { error, routeGroup });
    }
  }, [error, routeGroup]);

  // A driver's page lives at their own link, so the way forward is that
  // link again, not a dashboard they cannot open.
  const home =
    routeGroup === "driver-token"
      ? undefined
      : routeGroup === "public" || routeGroup === "authentication"
        ? { label: "Go to home page", to: "/" }
        : { label: "Go to dashboard", to: "/app" };

  return (
    <StatusPage
      action={
        <div className="flex flex-wrap justify-center gap-2">
          <Button onClick={() => window.location.reload()} type="button">
            Try again
          </Button>
          {home ? (
            <Button asChild variant="outline">
              <Link to={home.to}>{home.label}</Link>
            </Button>
          ) : null}
        </div>
      }
      description="This page didn't load. Try again, and if it keeps happening, email support@fleetminder.app."
      title="Something went wrong"
    />
  );
}

export function NotFoundPage() {
  const { status } = useSession();
  // The home page is the sign-up pitch, which is no way back for someone
  // already using FleetMinder.
  const home =
    status === "authenticated"
      ? { label: "Go to dashboard", to: "/app" }
      : { label: "Go to FleetMinder", to: "/" };

  return (
    <StatusPage
      action={
        <Button asChild>
          <Link to={home.to}>{home.label}</Link>
        </Button>
      }
      description="This link may be old or mistyped."
      title="Page not found"
    />
  );
}

type RouteFailureBoundaryProps = PropsWithChildren<{
  readonly routeGroup: string;
}>;

type RouteFailureBoundaryState = {
  readonly error?: Error;
};

export class RouteFailureBoundary extends Component<
  RouteFailureBoundaryProps,
  RouteFailureBoundaryState
> {
  override state: RouteFailureBoundaryState = {};

  static getDerivedStateFromError(error: Error): RouteFailureBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Route failed to render", {
      error,
      routeGroup: this.props.routeGroup,
      stack: info.componentStack,
    });
  }

  override render() {
    if (this.state.error) {
      return (
        <RouteGroupErrorPage
          error={this.state.error}
          routeGroup={this.props.routeGroup}
        />
      );
    }

    return this.props.children;
  }
}
